import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class H3Distance extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.h3distance',
        group: 'Spatial Indexes',
        title: 'H3 Grid Distance',
        description: `
      Compute the H3 grid distance between two H3 index columns creating a new table with the same
      columns as the source one and adding a new h3_distance column.
      The H3 distance is defined as the length of the shortest path between the cells in the graph
      formed by connecting adjacent cells.
      This function will return null if the cells are too far apart to compute the distance.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'h3_1',
          type: WorkflowComponentParamType.Column,
          title: 'First H3 index column',
          parent: 'source',
          dataType: [ColumnDataType.String]
        },
        {
          name: 'h3_2',
          type: WorkflowComponentParamType.Column,
          title: 'Second H3 index column',
          parent: 'source',
          dataType: [ColumnDataType.String]
        }
      ]
    )
    this.supportedProviders = [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.Postgres,
      Provider.DatabricksRest
    ]
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    let select = ''
    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Snowflake:
      case Provider.Postgres:
        select = `SELECT
                      ${connection.quoteName(
                        envVars.analyticsToolboxDataset as string
                      )}.H3_DISTANCE(
                          ${preparedParams.h3_1},
                          ${preparedParams.h3_2}
                      ) AS h3_distance,
                      *
                    FROM ${connection.quoteName(
                      preparedParams.source as string
                    )}
                    `
        break
      case Provider.DatabricksRest:
        select = `SELECT /*+ REPARTITION(40) */
                        H3_DISTANCE(
                            ${preparedParams.h3_1},
                            ${preparedParams.h3_2}
                        ) AS h3_distance,
                        *
                      FROM ${connection.quoteName(
                        preparedParams.source as string
                      )}
                      WHERE ${preparedParams.h3_1} IS NOT NULL
                        AND ${preparedParams.h3_2} IS NOT NULL
                      `
        break
    }
    let query = connection.getCreateTempTableQuery(
      outputTablename,
      select,
      codeType,
      null
    )
    if (connection.type === Provider.Postgres) {
      query = dedent`IF EXISTS(SELECT 1 FROM pg_extension WHERE extname = 'plv8') THEN
                          ${query}
                        ELSE
                          RAISE EXCEPTION 'plv8 extension not installed';
                        END IF;`
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.1.1'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.h3col}_distance`,
      type: ColumnDataType.Number
    })
  }
}

export { H3Distance }
